import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../theme";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VerifiedIcon from "@mui/icons-material/Verified";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import api from "../api/client";

const CommunityGpxFiles = ({ onClose, onSelectFile, embedded = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // Filters
  const [raceType, setRaceType] = useState("");
  const [country, setCountry] = useState("");
  const [courseProfile, setCourseProfile] = useState("");
  const [search, setSearch] = useState("");

  // Data
  const [allCourses, setAllCourses] = useState([]); // Unified list of official + community
  const [raceTypes, setRaceTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [courseProfiles, setCourseProfiles] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [raceTypesRes, filtersRes] = await Promise.all([
          api.get("/races/types"),
          api.get("/community/gpx-courses/filters"),
        ]);
        setRaceTypes(raceTypesRes.data);
        setCountries(filtersRes.data.countries);
        setCourseProfiles(filtersRes.data.course_profiles);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    loadFilterOptions();
  }, []);

  // Load all GPX courses (official + community combined) from unified view
  const loadAllCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new unified endpoint that queries the database view
      const response = await api.get("/community/gpx-courses?page_size=500");
      setAllCourses(response.data.courses);
    } catch (err) {
      setError(t("errorLoadingData", "Error loading GPX courses"));
      console.error("Error loading GPX courses:", err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load courses on mount
  useEffect(() => {
    loadAllCourses();
  }, [loadAllCourses]);

  // Handle download GPX (unified - works for both official and community)
  const handleDownload = async (course) => {
    try {
      const endpoint = course.is_official
        ? `/races/${course.original_id}/gpx`
        : `/community/gpx/${course.original_id}/download`;

      const response = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", course.filename || "course.gpx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading:", err);
    }
  };

  // Handle use for prediction (unified - works for both official and community)
  const handleUseForPrediction = async (course) => {
    try {
      const endpoint = course.is_official
        ? `/races/${course.original_id}/gpx`
        : `/community/gpx/${course.original_id}/download`;

      const response = await api.get(endpoint, { responseType: "blob" });
      const file = new File([response.data], course.filename || "course.gpx", {
        type: "application/gpx+xml"
      });

      if (onSelectFile) {
        onSelectFile(file);
        if (onClose) onClose();
      }
    } catch (err) {
      console.error("Error loading file for prediction:", err);
    }
  };

  // Unified GPX courses columns
  const unifiedColumns = [
    {
      field: "route_name",
      headerName: t("routeName", "Route Name"),
      flex: 1,
      minWidth: 200,
      display: "flex",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {params.row.is_official && (
            <Tooltip title={t("officialCourse", "Official Course")}>
              <VerifiedIcon fontSize="small" color="primary" />
            </Tooltip>
          )}
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: "sharer_name",
      headerName: t("sharedBy", "Shared By"),
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.row.is_official ? "primary" : "default"}
          variant={params.row.is_official ? "filled" : "outlined"}
        />
      )
    },
    {
      field: "race_type",
      headerName: t("raceType", "Race Type"),
      width: 130,
      renderCell: (params) => (
        params.value ? (
          <Chip
            size="small"
            label={params.value}
            variant="outlined"
          />
        ) : "-"
      )
    },
    {
      field: "country",
      headerName: t("country", "Country"),
      width: 120,
      renderCell: (params) => params.value || "-"
    },
    {
      field: "total_distance_km",
      headerName: t("distance", "Distance"),
      width: 100,
      renderCell: (params) => (params.value ? `${params.value.toFixed(1)}km` : "-")
    },
    {
      field: "total_elevation_m",
      headerName: t("elevation", "Elevation"),
      width: 100,
      renderCell: (params) => (params.value ? `${Math.round(params.value)}m` : "-")
    },
    {
      field: "course_profile",
      headerName: t("profile", "Profile"),
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip
            size="small"
            label={t(`profile_${params.value}`, params.value)}
            color={
              params.value === "flat"
                ? "success"
                : params.value === "rolling"
                ? "info"
                : params.value === "hilly"
                ? "warning"
                : "error"
            }
          />
        ) : "-"
      )
    },
    {
      field: "actions",
      headerName: t("actions", "Actions"),
      width: 150,
      sortable: false,
      display: "flex",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title={t("useForPrediction", "Use for Prediction")}>
            <IconButton
              size="small"
              onClick={() => handleUseForPrediction(params.row)}
              color="primary"
            >
              <PlayArrowIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("download", "Download")}>
            <IconButton size="small" onClick={() => handleDownload(params.row)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Filter courses based on current filters
  const getFilteredCourses = () => {
    return allCourses.filter((course) => {
      // Race type filter
      if (raceType && course.race_type !== raceType) return false;
      // Country filter
      if (country && course.country !== country) return false;
      // Course profile filter
      if (courseProfile && course.course_profile !== courseProfile) return false;
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesName = course.route_name?.toLowerCase().includes(searchLower);
        const matchesSharer = course.sharer_name?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesSharer) return false;
      }
      return true;
    });
  };

  const currentData = getFilteredCourses();
  const currentColumns = unifiedColumns;

  // Content wrapper - either Paper (standalone) or Box (embedded in drawer)
  const ContentWrapper = embedded ? Box : Paper;
  const wrapperProps = embedded
    ? { sx: { height: "100%", display: "flex", flexDirection: "column" } }
    : {
        elevation: 3,
        sx: {
          p: 3,
          backgroundColor: colors.grey[800],
          borderRadius: 2,
          minHeight: "500px",
          position: "relative"
        }
      };

  // Show login message for anonymous users
  if (!isAuthenticated) {
    return (
      <ContentWrapper {...wrapperProps}>
        {/* Header - only show when not embedded */}
        {!embedded && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              {t("communityGpxFiles", "Community GPX Courses")}
            </Typography>
            {onClose && (
              <IconButton onClick={onClose} sx={{ color: colors.grey[300] }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        )}

        {/* Login Required Message */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 400,
            textAlign: "center",
            p: 4
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 64, color: colors.blueAccent[400], mb: 2 }} />
          <Typography variant="h5" color={colors.grey[100]} mb={2}>
            {t("loginRequired", "Login Required")}
          </Typography>
          <Typography variant="body1" color={colors.grey[300]} sx={{ maxWidth: 400 }}>
            {t("communityLoginMessage", "Please log in to unlock this feature. We are building a community to help us fine-tune the engine, and your participation makes mkpace better for everyone.")}
          </Typography>
        </Box>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper {...wrapperProps}>
      {/* Header - only show when not embedded */}
      {!embedded && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
            {t("communityGpxFiles", "Community GPX Courses")}
          </Typography>
          {onClose && (
            <IconButton onClick={onClose} sx={{ color: colors.grey[300] }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: colors.grey[300] }}>{t("raceType", "Race Type")}</InputLabel>
            <Select
              value={raceType}
              label={t("raceType", "Race Type")}
              onChange={(e) => {
                setRaceType(e.target.value);
                setPage(0);
              }}
              sx={{
                color: colors.grey[100],
                '.MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '.MuiSvgIcon-root': { color: colors.grey[300] }
              }}
            >
              <MenuItem value="">{t("all", "All")}</MenuItem>
              {raceTypes.map((type) => (
                <MenuItem key={type.id} value={type.name}>
                  {type.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: colors.grey[300] }}>{t("country", "Country")}</InputLabel>
            <Select
              value={country}
              label={t("country", "Country")}
              onChange={(e) => {
                setCountry(e.target.value);
                setPage(0);
              }}
              sx={{
                color: colors.grey[100],
                '.MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '.MuiSvgIcon-root': { color: colors.grey[300] }
              }}
            >
              <MenuItem value="">{t("all", "All")}</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: colors.grey[300] }}>{t("courseProfile", "Profile")}</InputLabel>
            <Select
              value={courseProfile}
              label={t("courseProfile", "Profile")}
              onChange={(e) => {
                setCourseProfile(e.target.value);
                setPage(0);
              }}
              sx={{
                color: colors.grey[100],
                '.MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
                '.MuiSvgIcon-root': { color: colors.grey[300] }
              }}
            >
              <MenuItem value="">{t("all", "All")}</MenuItem>
              {courseProfiles.map((p) => (
                <MenuItem key={p} value={p}>
                  {t(`profile_${p}`, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder={t("search", "Search...")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                color: colors.grey[100],
                '& fieldset': { borderColor: colors.grey[600] },
                '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] }
              },
              '& .MuiInputBase-input::placeholder': { color: colors.grey[400] }
            }}
          />
        </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 400, width: "100%" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={currentData}
            columns={currentColumns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            paginationMode="client"
            rowCount={currentData.length}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            disableSelectionOnClick
            onRowDoubleClick={(params) => {
              handleUseForPrediction(params.row);
            }}
            sx={{
              border: "none",
              color: colors.grey[100],
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${colors.grey[700]}`,
                color: colors.grey[100]
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.grey[800],
                borderBottom: `1px solid ${colors.grey[700]}`,
                color: colors.grey[100]
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: colors.grey[100],
                fontWeight: "bold"
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${colors.grey[700]}`,
                backgroundColor: colors.grey[800],
                color: colors.grey[100]
              },
              "& .MuiTablePagination-root": {
                color: colors.grey[100]
              },
              "& .MuiSvgIcon-root": {
                color: colors.grey[300]
              }
            }}
          />
        )}
      </Box>

    </ContentWrapper>
  );
};

export default CommunityGpxFiles;
